import type { LeadGenerationListApiData } from '@navio/nsdk';
import { LeadGenerationRequester, LeadStage } from '@navio/nsdk';
import { filterUnitFor, filterUrlState } from 'noddi-ui-web';
import { LEAD_GENERATION_TYPE_OPTIONS, LEAD_STAGE_OPTIONS } from 'noddi-util';
import { LEAD_GENERATION_REQUESTER_FILTER_OPTIONS } from './useLeadGenerationRequestedByFilter';

type LeadGenerationListFilterInput = Omit<
  NonNullable<LeadGenerationListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

export const leadGenerationFilterUnit = filterUnitFor<LeadGenerationListFilterInput>();

/** Leads still in play until the user picks; shared by the lead generation and tire offer leads lists. */
export const leadGenerationStageFilter = leadGenerationFilterUnit.multiSelect('stages', {
  title: 'Stage',
  description: 'Filter leads by where they are in the sales funnel',
  options: LEAD_STAGE_OPTIONS,
  defaults: [LeadStage.OPEN, LeadStage.OFFERED],
  exclude: true
});

export const leadGenerationTypeFilter = leadGenerationFilterUnit.multiSelect('types', {
  title: 'Type',
  description: 'Filter lead generations by their type',
  options: LEAD_GENERATION_TYPE_OPTIONS,
  exclude: true
});

export const leadGenerationTireHotelCustomerFilter = leadGenerationFilterUnit.boolean('isTireHotelCustomer', {
  title: 'Tire hotel customer',
  description: 'Filter by whether the customer is a tire hotel customer.'
});

/** Reset option of the quick filter — clears the scope rather than naming a requester. */
const REQUESTED_BY_ALL = 'all';

const REQUESTED_BY_URL_KEY = filterUrlState.urlKey('requestedBy');

const parseRequester = (value: string) =>
  LEAD_GENERATION_REQUESTER_FILTER_OPTIONS.find((option) => option.value === value)?.value;

/**
 * Worker or customer leads, as a sidebar facet and as the table's quick filter: two controls on the one
 * `requestedBy` param, so the tray and the facet never disagree. No Exclude: with two requesters, excluding one
 * is picking the other.
 */
export const leadGenerationRequestedByFilter = leadGenerationFilterUnit.compose(
  'requestedBy',
  { [REQUESTED_BY_URL_KEY]: filterUrlState.stored<LeadGenerationRequester>(String, parseRequester) },
  (values, setValues) => {
    const requestedBy = filterUrlState.storedValue(
      values[REQUESTED_BY_URL_KEY] as LeadGenerationRequester | null | undefined,
      undefined
    );
    const set = (value: string | number | null) =>
      setValues(() => ({ [REQUESTED_BY_URL_KEY]: value == null ? undefined : parseRequester(String(value)) }));
    return {
      controls: {
        controls: [
          {
            id: 'requestedBy',
            type: 'single-select',
            title: 'Requested by',
            description: 'Filter by whether a worker or the customer asked for the lead',
            options: LEAD_GENERATION_REQUESTER_FILTER_OPTIONS,
            canExclude: false,
            value: { include: requestedBy ?? null, exclude: [] },
            onChange: (next) => set(next.include),
            reset: () => set(null),
            isInitial: values[REQUESTED_BY_URL_KEY] === undefined
          }
        ],
        quickFilter: {
          label: 'Requested by',
          options: [
            { value: REQUESTED_BY_ALL, label: 'All' },
            { value: LeadGenerationRequester.CUSTOMER, label: 'Requested by customer' },
            { value: LeadGenerationRequester.WORKER, label: 'From worker' }
          ],
          selectedValue: requestedBy ?? REQUESTED_BY_ALL,
          onChange: (value) => set(value === REQUESTED_BY_ALL ? null : value)
        }
      },
      params: { requestedBy }
    };
  }
);
